import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaTrash } from 'react-icons/fa';
import dayjs from 'dayjs';
import CategoryFormModal from './CategoryFormModal';
import CategoryViewModal from './CategoryViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const CategoryMaster = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/categories`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      console.log('Categories API response:', data);
      // Ensure data is an array
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    if (!Array.isArray(categories)) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedCategories = [...categories].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedDate') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'CategoryCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setCategories(sortedCategories);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setShowView(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const response = await fetch(`${API_URL}/categories/${category.CategoryCode}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete category');
      }
      
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.message || 'An error occurred while deleting the category');
    }
  };

  const handleDownloadCSV = () => {
    if (!Array.isArray(categories) || categories.length === 0) {
      alert('No categories to export');
      return;
    }
    
    const headers = [
      'Category Code', 'Category Name', 'Description', 'Subcategory Count', 'Status', 'Created Date'
    ];
    const rows = categories.map(c => [
      c.CategoryCode, c.CategoryName, c.Description || '-', c.SubcategoryCount || 0, c.Status, 
      dayjs(c.CreatedDate).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'categories.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedCategory(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedCategory) {
        const response = await fetch(`${API_URL}/categories/${selectedCategory.CategoryCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update category');
        }
      } else {
        const response = await fetch(`${API_URL}/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create category');
        }
      }
      setShowForm(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.message || 'An error occurred while saving the category');
    } finally {
      setFormLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main
      className="flex-1 p-2 md:p-8 overflow-hidden ml-0 md:ml-16 transition-all duration-300"
    >        <div className="flex flex-col h-full">
          <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center sm:items-center mb-4 md:mb-6 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">Category Master</h1>
            <button
              className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-full sm:w-auto justify-center"
              onClick={handleCreate}
            >
              <FaPlus /> Create New Category
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 min-w-[800px]">
                <thead>
                  <tr>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CategoryCode')}
                    >
                      <div className="flex items-center justify-center">
                        Category Code {getSortIcon('CategoryCode')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CategoryName')}
                    >
                      <div className="flex items-center justify-center">
                        Category Name {getSortIcon('CategoryName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Description')}
                    >
                      <div className="flex items-center justify-center">
                        Description {getSortIcon('Description')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('SubcategoryCount')}
                    >
                      <div className="flex items-center justify-center">
                        Subcategories {getSortIcon('SubcategoryCount')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Status')}
                    >
                      <div className="flex items-center justify-center">
                        Status {getSortIcon('Status')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CreatedDate')}
                    >
                      <div className="flex items-center justify-center">
                        Created Date {getSortIcon('CreatedDate')}
                      </div>
                    </th>
                    <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        Loading categories...
                      </td>
                    </tr>
                  ) : categories && categories.length > 0 ? categories.map((category, idx) => (
                    <tr
                      key={category.CategoryCode}
                      className={`transition-all duration-300 group hover:bg-[#eaf6f6] hover:shadow-md border-b last:border-b-0 transform hover:scale-[1.01]`}
                      style={{ boxShadow: idx === categories.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                        <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => handleView(category)}>{category.CategoryCode}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-semibold">{category.CategoryName}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm break-all max-w-[200px]">{category.Description || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#52ab98] text-white">
                          {category.SubcategoryCount || 0}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                          ${category.Status === 'Active' ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500'}`}>
                          {category.Status}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                        {category.CreatedDate ? (
                          <>
                            {dayjs(category.CreatedDate).format('YYYY-MM-DD')}
                            <br />
                            <span className="text-xs text-gray-400">{dayjs(category.CreatedDate).format('HH:mm')}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(category)}>
                          <FaEye />
                        </button>
                        <button title="Edit" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(category)}>
                          <FaEdit />
                        </button>
                        <button 
                          title={category.SubcategoryCount > 0 ? 
                            `Cannot delete: This category has ${category.SubcategoryCount} subcategory(ies). Delete subcategories first.` : 
                            "Delete"
                          } 
                          className={`p-2 rounded-full transition-all duration-200 hover:scale-110 ${
                            category.SubcategoryCount > 0 
                              ? 'hover:bg-yellow-100 text-yellow-600 cursor-not-allowed opacity-60' 
                              : 'hover:bg-red-100 text-red-500'
                          }`} 
                          onClick={() => category.SubcategoryCount > 0 ? 
                            alert(`Cannot delete this category. It has ${category.SubcategoryCount} subcategory(ies). Please delete all subcategories first.`) : 
                            handleDelete(category)
                          }
                        >
                          <FaTrash />
                        </button>
                        <button title="Download CSV" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={handleDownloadCSV}>
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        No categories found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <CategoryFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedCategory}
            isEdit={isEdit}
            loading={formLoading}
          />
          <CategoryViewModal
            open={showView}
            onClose={() => setShowView(false)}
            category={selectedCategory}
          />
        </div>
      </main>
    </div>
  );
};

export default CategoryMaster; 